import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { getWallet } from '../wallets/api';
import { ActivityDashboard } from './ActivityDashboard';
import { AgentBalancesCard } from './AgentBalancesCard';
import { listRuns, type Agent, type AgentRun, type AgentTemplate } from './api';

type Props = {
  orgId: string;
  agent: Agent;
  template?: AgentTemplate;
};

export function AgentOverviewTab({ orgId, agent, template }: Props) {
  const runs = useQuery({
    queryKey: ['runs', orgId, agent.id],
    queryFn: () => listRuns(orgId, agent.id),
    enabled: !!orgId,
    refetchInterval: 5_000,
  });

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <ActivityDashboard orgId={orgId} agentId={agent.id} />
        <RecentRunsCard agentId={agent.id} runs={runs.data ?? []} />
      </div>
      <div className="space-y-6">
        {agent.wallet_id && <AgentBalancesCard orgId={orgId} agentId={agent.id} />}
        <IdentityCard orgId={orgId} agent={agent} template={template} />
      </div>
    </div>
  );
}

function IdentityCard({
  orgId,
  agent,
  template,
}: {
  orgId: string;
  agent: Agent;
  template?: AgentTemplate;
}) {
  const wallet = useQuery({
    queryKey: ['wallet-for-agent', orgId, agent.wallet_id],
    queryFn: () => getWallet(orgId, agent.wallet_id!),
    enabled: !!orgId && !!agent.wallet_id,
  });

  const effectivePrompt =
    agent.custom_system_prompt && agent.custom_system_prompt.trim().length > 0
      ? agent.custom_system_prompt
      : template?.system_prompt;
  const promptIsOverride =
    !!agent.custom_system_prompt && agent.custom_system_prompt.trim().length > 0;

  return (
    <Card>
      <CardHeader title="Identity" description="Wallet, model and prompt that shape this agent." />
      <CardBody className="space-y-4 text-sm">
        {template && (
          <Row label="What it does">
            <span className="text-ink-secondary">{template.description}</span>
          </Row>
        )}
        {agent.description && (
          <Row label="Operator notes">
            <span className="text-ink-primary">{agent.description}</span>
          </Row>
        )}
        {agent.specialization && (
          <Row label="Specialization">
            <span className="whitespace-pre-wrap text-ink-primary">{agent.specialization}</span>
          </Row>
        )}
        <Row label="Model">
          <span className="font-mono text-xs">{agent.model}</span>
        </Row>
        {agent.wallet_id && (
          <Row label="Wallet">
            <div className="space-y-1">
              <div className="font-mono break-all text-xs text-ink-primary">
                {wallet.data?.address ?? <span className="text-ink-tertiary">loading…</span>}
              </div>
              {wallet.data && (
                <div className="text-[11px] text-ink-tertiary">
                  {wallet.data.curve} · {wallet.data.status}
                </div>
              )}
            </div>
          </Row>
        )}
        {effectivePrompt && (
          <details className="group rounded-lg border border-line-soft bg-surface-soft/40">
            <summary className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-xs text-ink-secondary hover:text-ink-primary">
              <span className="flex items-center gap-2">
                <span className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
                  System prompt
                </span>
                <span className="font-mono text-[10px] uppercase text-ink-tertiary">
                  ({promptIsOverride ? 'operator override' : 'from template'})
                </span>
              </span>
              <span className="font-mono text-[10px] text-ink-tertiary transition-transform group-open:rotate-180">
                ▾
              </span>
            </summary>
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap border-t border-line-soft bg-surface-white px-3 py-3 text-xs leading-relaxed text-ink-primary">
              {effectivePrompt}
            </pre>
          </details>
        )}
      </CardBody>
    </Card>
  );
}

function RecentRunsCard({ agentId, runs }: { agentId: string; runs: AgentRun[] }) {
  const recent = runs.slice(0, 5);
  return (
    <Card>
      <CardHeader
        title="Recent runs"
        description="Latest 5. Polled every 5 seconds. Open the Runs tab for the full list."
      />
      <CardBody className="p-0">
        {recent.length === 0 ? (
          <p className="px-4 py-6 text-sm text-ink-tertiary">No runs yet.</p>
        ) : (
          <ul className="divide-y divide-line-soft">
            {recent.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/agents/${agentId}/runs/${r.id}`}
                  className="block px-4 py-3 hover:bg-surface-soft/40"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="truncate text-sm text-ink-primary">
                      {truncate(r.summary || r.objective, 90)}
                    </span>
                    <RunStatusBadge status={r.status} outcome={r.outcome} />
                  </div>
                  <div className="mt-1 flex items-center gap-3 font-mono text-[10px] text-ink-tertiary">
                    <span>{new Date(r.created_at).toLocaleString()}</span>
                    <span>
                      {r.tokens_input}/{r.tokens_output} tok
                    </span>
                    {r.outcome && <span>· {r.outcome}</span>}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function RunStatusBadge({ status, outcome }: { status: string; outcome?: string }) {
  const map: Record<string, string> = {
    pending: 'bg-surface-soft text-ink-tertiary',
    running: 'bg-amber-100 text-amber-700',
    done: 'bg-ok/10 text-ok',
    failed: 'bg-danger/10 text-danger',
  };
  const label = outcome === 'timeout' ? 'timeout' : status;
  return (
    <span
      className={`shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px] uppercase ${
        map[status] ?? ''
      }`}
    >
      {label}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="text-xs uppercase tracking-eyebrow text-ink-tertiary">{label}</div>
      <div className="col-span-2 text-sm text-ink-primary">{children}</div>
    </div>
  );
}

function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
